import React, { useRef, useEffect } from 'react';
import JoditEditor from 'jodit-react';
import '../../../Styles/TextEditor.css';

const JODIT_COMMON_CONFIG = {
    hidePoweredByJodit: true,
    askBeforePasteHTML: false,
    askBeforePasteFromWord: false,
    defaultActionOnPaste: 'insert_only_text',
    defaultActionOnPasteFromWord: 'insert_only_text',
    processPasteHTML: false,
    showXPathInStatusbar: false,
    showCharsCounter: true,
    showWordsCounter: true,
    toolbarAdaptive: false,
    minHeight: 280,
    maxHeight: 480,
    removeButtons: [
        'link', 'table', 'hr', 'symbol', 'math', 'image', 'video', 'file',
        'speechRecognize', 'print', 'about', 'preview', 'classSpan',
        'superscript', 'subscript', 'lineHeight', 'font', 'copyformat'
    ],
    disablePlugins: [
        'about', 'powered-by-jodit', 'poweredByJodit', 'symbol', 'math', 'image', 'video', 'file', 'link', 'table'
    ],
};

const JODIT_VIEW_CONFIG = {
    ...JODIT_COMMON_CONFIG,
    readonly: true,
    toolbar: false,
    buttons: [],
};

const JODIT_EDIT_CONFIG = {
    ...JODIT_COMMON_CONFIG,
    readonly: false,
    placeholder: 'Type or edit text content...',
    buttons: [
        'bold', 'italic', 'underline', 'strikethrough', 'eraser', '|',
        'ul', 'ol', '|',
        'paragraph', 'fontsize', '|',
        'brush', '|',
        'left', 'center', 'right', 'justify', '|',
        'undo', 'redo', '|',
        'cut', 'copy', 'paste', 'selectall', '|',
        'find', 'source'
    ],
};

export default function AiTextEditor({
    value = '',
    onChange,
    readOnly = false,
    placeholder = 'Type or paste content here...',
    minHeight = 280,
    maxHeight = 500,
    className = '',
}) {
    const editorRef = useRef(null);
    const wrapperRef = useRef(null);
    const content = value || '';
    const baseConfig = readOnly ? JODIT_VIEW_CONFIG : JODIT_EDIT_CONFIG;

    const selectEditorContents = () => {
        if (!wrapperRef.current) return;
        const wysiwyg = wrapperRef.current.querySelector('.jodit-wysiwyg');
        if (wysiwyg) {
            const range = document.createRange();
            range.selectNodeContents(wysiwyg);
            const sel = window.getSelection();
            sel.removeAllRanges();
            sel.addRange(range);
        }
    };

    const config = {
        ...baseConfig,
        events: {
            beforeCommand: (command) => {
                if (command && String(command).toLowerCase() === 'selectall') {
                    selectEditorContents();
                    return false;
                }
            },
        },
    };

    useEffect(() => {
        const wrapper = wrapperRef.current;
        if (!wrapper) return;

        const handleKeyDownCapture = (e) => {
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'a') {
                const wysiwyg = wrapper.querySelector('.jodit-wysiwyg');
                const isInside = wrapper.contains(document.activeElement) || (wysiwyg && wysiwyg.contains(e.target));
                if (isInside) {
                    e.preventDefault();
                    e.stopPropagation();
                    selectEditorContents();
                }
            }
        };

        wrapper.addEventListener('keydown', handleKeyDownCapture, true);
        return () => {
            wrapper.removeEventListener('keydown', handleKeyDownCapture, true);
        };
    }, []);

    return (
        <div
            ref={wrapperRef}
            className={`hc-ai-text-editor-jodit-wrap ${readOnly ? 'hc-ai-text-editor-readonly' : ''} ${className}`}
            style={{
                '--ai-editor-min-height': typeof minHeight === 'number' ? `${minHeight}px` : minHeight || '280px',
                '--ai-editor-max-height': typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight || '500px',
            }}
        >
            <JoditEditor
                ref={editorRef}
                value={content}
                config={config}
                onBlur={(newContent) => {
                    if (!readOnly && onChange) {
                        onChange(newContent);
                    }
                }}
                onChange={() => {}}
            />
        </div>
    );
}
